import React, { memo } from 'react';
import {
  FormControlLabel,
  RadioGroup as MuiRadioGroup,
  Radio as MuiRadio,
  FormControl,
  FormLabel,
} from '@mui/material';

import { ReactComponent as RadioDefault } from '../../../../assets/icons/RadioDefault.svg';
import { ReactComponent as RadioChecked } from '../../../../assets/icons/RadioChecked.svg';

import { useStyles } from './styles';
import ControlledError from '../ControlledError';

export interface IRadioGroupProps {
  value?: string;
  label?: string;
  disabled?: boolean;
  styleClass?: string;
  options: Array<{ value: string; label: string; radioProps?: any }>;
  row?: boolean;
  error?: string;
  required?: boolean;
  skippable?: boolean;
  /** Base id for automation. The root FormControl uses `${testId}`, each option's radio input
   * `${testId}-option-{value}`. */
  testId?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Radio = (props: React.ComponentProps<typeof MuiRadio>) => {
  const classes = useStyles();
  return <MuiRadio {...props} className={`${classes.radio} ${props.className || ''}`} />;
};

const RadioGroup = ({
  value,
  label,
  disabled = false,
  styleClass,
  options,
  row,
  error,
  required,
  skippable = false,
  testId,
  onChange,
}: IRadioGroupProps) => {
  const classes = useStyles();
  return (
    <ControlledError show={!!error} error={!!error && error}>
      <FormControl disabled={disabled} className={`${classes.formControl} ${styleClass || ''}`} data-testid={testId}>
        {label && (
          <FormLabel>
            {label}
            {(required || skippable) && (
              <span className={required ? classes.requiredMark : classes.skippableMark}>{required ? '*' : '**'}</span>
            )}
          </FormLabel>
        )}
        <MuiRadioGroup value={value} row={row} onChange={onChange}>
          {options.map(option => (
            <FormControlLabel
              key={option.value || option.label}
              value={option.value}
              control={
                <Radio
                  icon={<RadioDefault />}
                  checkedIcon={<RadioChecked />}
                  disableRipple
                  className={classes.radio}
                  inputProps={
                    testId
                      ? ({ 'data-testid': `${testId}-option-${option.value || option.label}` } as any)
                      : undefined
                  }
                  {...option.radioProps}
                />
              }
              label={option.label}
              className={row ? classes.radioLabelRow : ''}
            />
          ))}
        </MuiRadioGroup>
      </FormControl>
    </ControlledError>
  );
};

export default memo(RadioGroup);
